import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";

import { Button, Collapsible, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import { useExportReportTemplateJsonQuery } from "@api/graphql/types-and-hooks";

interface TemplateExportJsonProps {
  templateId: string;
}

export const TemplateExportJson: React.FC<TemplateExportJsonProps> = ({ templateId }) => {
  const [copied, setCopied] = useState(false);

  const exportQuery = useExportReportTemplateJsonQuery(
    { id: templateId },
    {
      queryKey: ["export-template-json", templateId],
      enabled: !!templateId, // only run if templateId exists
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const handleExport = async () => {
    try {
      // Trigger a refetch if needed
      const { data } = await exportQuery.refetch();
      const jsonString = data?.exportReportTemplateJson?.json;
      if (jsonString) {
        await navigator.clipboard.writeText(jsonString);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error("Failed to export template JSON", err);
      alert("Failed to export template JSON");
    }
  };

  return (
    <div className="bg-white rounded-2xl pt-4 pb-2 pl-4 pr-0 border-limestone-100 border w-full">
      <Collapsible
        defaultOpenState={true}
        headerContent={
          <div className="flex space-x-2 items-center">
            <DocumentDuplicateIcon className="h-4 w-4 text-text-secondary" />
            <Small displayType="loud" colorType="primary">
              Export Template JSON
            </Small>
          </div>
        }
        headerIsClickable={true}
        bodyContent={
          <div className="p-2 flex flex-col gap-2">
            <Paragraph colorType="secondary">
              Export your report template as JSON. You can copy it to clipboard or use it to import into another
              environment.
            </Paragraph>
            <Button
              text={
                copied ? (
                  <div className="flex items-center justify-center h-full w-full text-rio-700">
                    <Small colorType="primary" className="text-rio-700 text-xs">
                      Copied!
                    </Small>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full w-full text-rio-700">
                    <Small colorType="primary" className="text-rio-700 text-xs">
                      Copy JSON to Clipboard
                    </Small>
                  </div>
                )
              }
              appearance="secondary"
              className="ring-rio-400 mt-5 min-w-full max-h-8 flex items-center justify-center"
              onClick={handleExport}
              disabled={exportQuery.isFetching}
            />
            {exportQuery.data?.exportReportTemplateJson?.json && (
              <pre className="mt-2 max-h-64 overflow-y-auto p-2 bg-white dark:bg-slate-900 rounded-sm text-xs text-gray-800 dark:text-gray-200">
                {exportQuery.data.exportReportTemplateJson.json}
              </pre>
            )}
            {exportQuery.error && <Paragraph colorType="secondary">Failed to load JSON</Paragraph>}
          </div>
        }
      />
    </div>
  );
};
